import React, { useState, useEffect, useCallback } from 'react'; // Ajoutez useCallback
import { OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { toast } from 'sonner';
import useSelectedDomain from '../../service/Hooks/useSelectedDomain';
import { getPaginatedAccountValidators, deleteValidator } from '../../service/api/validatorService'; // Importez deleteValidator
import NewValidatorModal from './NewValidatorModal';
import ConfirmationModal from './ConfirmationModal';
import { FaEdit, FaTrash } from 'react-icons/fa';


function ValidateurCompte() {
  const [validators, setValidators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 5;

  const selectedDomain = useSelectedDomain();
  const domainId = selectedDomain?.id;

  // --- États pour les modales de modification et suppression ---
  const [showValidatorFormModal, setShowValidatorFormModal] = useState(false); // Pour le formulaire de création/modification
  const [validatorToEdit, setValidatorToEdit] = useState(null); // Pour stocker le validateur à modifier

  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [validatorToDelete, setValidatorToDelete] = useState(null);


  // --- Récupération des validateurs de compte ---
  const fetchValidators = useCallback(async (currentPage = page) => {
    if (!domainId) {
      setLoading(false);
      setValidators([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await getPaginatedAccountValidators(domainId, currentPage, pageSize);
      setValidators(data.items || []);
      setTotalPages(data.totalPages || 1);
      setPage(data.pageNumber || 1);
    } catch (err) {
      setError(err.message || "Impossible de récupérer les validateurs de compte.");
      setValidators([]);
    } finally {
      setLoading(false);
    }
  }, [domainId, page, pageSize]); // Dépendances pour useCallback

  useEffect(() => {
    fetchValidators();
  }, [domainId, page, fetchValidators]); // Ajoutez fetchValidators aux dépendances de useEffect

  // --- Gestionnaires de modale pour l'ajout/modification ---
  // Note: ValidateurCompte n'a pas de bouton "Nouveau Validateur" par défaut dans votre code,
  // mais cette fonction est laissée pour la cohérence si vous l'ajoutez plus tard.
  // const handleOpenNewValidatorModal = () => {
  //   if (!domainId) {
  //     toast.warn("Veuillez d'abord sélectionner un domaine.");
  //     return;
  //   }
  //   setValidatorToEdit(null); // S'assurer que c'est le mode création
  //   setShowValidatorFormModal(true);
  // };

  const handleOpenEditValidatorModal = (validator) => {
    setValidatorToEdit(validator); // Passer le validateur à modifier
    setShowValidatorFormModal(true);
  };

  const handleCloseValidatorFormModal = () => {
    setShowValidatorFormModal(false);
    setValidatorToEdit(null); // Réinitialiser le validateur à modifier à la fermeture
  };

  const handleValidatorOperationSuccess = () => {
    toast.success("Opération réussie ! La liste est mise à jour.");
    fetchValidators(page); // Rafraîchit la liste en restant sur la page actuelle
  };


  // --- Gestionnaires pour la suppression (inchangés) ---
  const handleOpenDeleteConfirmation = (validator) => {
    setValidatorToDelete(validator);
    setShowDeleteConfirmationModal(true);
  };

  const handleCloseDeleteConfirmation = () => {
    setShowDeleteConfirmationModal(false);
    setValidatorToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (validatorToDelete) {
      setLoading(true);
      try {
        await deleteValidator(validatorToDelete.id);
        toast.success(`Validateur "${validatorToDelete.accountName || validatorToDelete.firstName + ' ' + validatorToDelete.lastName}" supprimé avec succès !`);
        fetchValidators(page);
      } catch (err) {
        toast.error(`Erreur lors de la suppression du validateur: ${err.message || "Une erreur est survenue."}`);
      } finally {
        setLoading(false);
        handleCloseDeleteConfirmation();
      }
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-end mb-4 p-3 bg-white shadow-sm rounded">
        <h4 className="fw-bold text-primary text-uppercase mb-3 m-0 text-center" style={{ letterSpacing: "1px", textShadow: "1px 1px 2px rgba(0, 0, 0, 0.2) " }}>
          Validateurs De Compte
        </h4>
      </div>

      <div className="card lastest_trans">
        <div className="card-body px-3 py-0">
          <div className="table-responsive">
            <table className="table trans-table table-striped table-hover border-no tbl-btn short-one">
              <thead className="text-center fw-bold fs-1">
                <tr>
                  <th className="fw-bold fs-4">Nom Validateur</th>
                  <th className="fw-bold fs-4">Nom Domaine</th>
                  <th className="fw-bold fs-4">Type de Validation</th>
                  <th className="fw-bold fs-4">Actions</th>
                </tr>
              </thead>
              <tbody className="text-center fw-bold fs-5">
                {loading ? (
                  <tr>
                    <td colSpan="4" className="text-center">
                      <Spinner animation="border" variant="primary" />
                      <p>Chargement des validateurs...</p>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="4" className="text-danger text-center">Erreur: {error}</td>
                  </tr>
                ) : validators.length > 0 ? (
                  validators.map((validator) => (
                    <tr key={validator.id}>
                      <td>{validator.accountName || `${validator.firstName} ${validator.lastName}`}</td>
                      <td>{validator.domainName || selectedDomain?.name}</td>
                      <td>Compte</td>
                      <td>
                        <div>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Modifier</Tooltip>}>
                          <button
                              className="btn btn-primary shadow btn-xs sharp me-1"
                              onClick={(e) => { e.preventDefault(); handleOpenEditValidatorModal(validator); }}
                          >
                              <FaEdit />
                          </button>
                          </OverlayTrigger>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer</Tooltip>}>
                            <button onClick={(e) => { e.preventDefault(); handleOpenDeleteConfirmation(validator); }} className="btn btn-danger shadow btn-xs sharp">
                              <FaTrash />
                            </button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center text-bg-light">Aucun validateur de compte trouvé pour ce domaine.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Contrôles de pagination */}
          {totalPages > 1 && (
            <nav className="mt-3">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${page <= 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setPage(prev => Math.max(prev - 1, 1))}>
                    <i className="fa-solid fa-angle-left"></i>
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => (
                  <li key={i} className={`page-item ${page === i + 1 ? "active" : ""}`}>
                    <button className="page-link" onClick={() => setPage(i + 1)}>
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${page >= totalPages ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}>
                    <i className="fa-solid fa-angle-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </div>

      {/* Modale de formulaire (création/modification) */}
      <NewValidatorModal
        show={showValidatorFormModal}
        onClose={handleCloseValidatorFormModal}
        onValidatorCreated={handleValidatorOperationSuccess}
        validatorToEdit={validatorToEdit}
      />

      {/* Modale de confirmation de suppression */}
      <ConfirmationModal
        show={showDeleteConfirmationModal}
        onClose={handleCloseDeleteConfirmation}
        onConfirm={handleConfirmDelete}
        title="Confirmer la suppression du validateur"
        message={
          validatorToDelete ? (
            <>
              Êtes-vous sûr de vouloir supprimer le validateur&nbsp;
              <strong className="text-uppercase fs-4 p-1 text-dark">
                {validatorToDelete.accountName
                  ? validatorToDelete.accountName
                  : `${validatorToDelete.firstName} ${validatorToDelete.lastName}`}
              </strong>
              &nbsp;?
            </>
          ) : (
            "Êtes-vous sûr de vouloir supprimer ce validateur ?"
          )
        }
      />
    </>
  );
}

export default ValidateurCompte;